import axios from "axios";
import { CapacitorHttp } from "@capacitor/core";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/**
 * Résout une URL d'image / de média venue du backend en URL absolue.
 *
 * Sur le web, les URLs relatives (ex: `/api/files/branding/xxx.png`) se
 * résolvent naturellement contre l'origine courante. Mais dans le wrapper
 * Capacitor iOS/Android, l'origine est `capacitor://localhost/`, ce qui
 * casse toute URL relative servie par notre backend distant → l'image
 * s'affiche avec un point d'interrogation bleu.
 *
 * Ce helper préfixe donc systématiquement les chemins relatifs avec
 * `REACT_APP_BACKEND_URL`. Les URLs déjà absolues (http/https/data:) sont
 * retournées telles quelles.
 */
export function resolveMediaUrl(url) {
  if (!url || typeof url !== "string") return url;
  const trimmed = url.trim();
  if (!trimmed) return trimmed;
  if (/^(https?:|data:|blob:|capacitor:)/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("/")) return `${BACKEND_URL}${trimmed}`;
  return trimmed;
}

/* ─────────────────────────────────────────────────────────────────────
   Capacitor native HTTP adapter.

   On iOS WKWebView, cross-origin fetch/XHR to saor.online is flaky
   (intermittent "Impossible de joindre le serveur" errors that don't show
   up in Safari). The official fix is to route requests through Capacitor's
   native HTTP plugin (URLSession on iOS, OkHttp on Android), which bypasses
   the WebView's network stack entirely.

   We do NOT enable the global `CapacitorHttp.enabled: true` setting because
   it also patches the `fetch` used by Mapbox to load map tiles, breaking
   the basemap (binary streaming responses are mis-handled). Instead we
   plug into axios via a custom adapter that activates only when running
   inside the Capacitor wrapper. All other transports (Mapbox, Firebase
   RTDB, OneSignal Web SDK …) keep using the native browser fetch and
   function normally.
   ───────────────────────────────────────────────────────────────────── */
const isNative =
  typeof window !== "undefined" &&
  !!window.Capacitor &&
  typeof window.Capacitor.isNativePlatform === "function" &&
  window.Capacitor.isNativePlatform();

async function capacitorHttpAdapter(config) {
  // Resolve full URL (axios stores baseURL + url + params separately).
  let url = config.url || "";
  if (!/^https?:\/\//i.test(url)) {
    url = (config.baseURL || "").replace(/\/+$/, "") + "/" + url.replace(/^\/+/, "");
  }
  if (config.params && Object.keys(config.params).length) {
    const sep = url.includes("?") ? "&" : "?";
    url +=
      sep +
      Object.entries(config.params)
        .filter(([, v]) => v !== undefined && v !== null)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join("&");
  }

  const headers = { ...(config.headers || {}) };
  delete headers["common"];
  delete headers["delete"];
  delete headers["get"];
  delete headers["head"];
  delete headers["post"];
  delete headers["put"];
  delete headers["patch"];

  const data = config.data;
  const isFormData = typeof FormData !== "undefined" && data instanceof FormData;
  const isBlob = typeof Blob !== "undefined" && data instanceof Blob;
  const wantsBinaryResponse = config.responseType === "blob" || config.responseType === "arraybuffer";

  // FormData uploads can't go through CapacitorHttp's JSON marshalling —
  // keep them on native fetch (backend routes accepting multipart are
  // same-origin so no CORS/WKWebView flakiness).
  if (isFormData || isBlob) {
    // ⚠️  Ne PAS forcer de Content-Type sur les uploads multipart —
    // le browser DOIT générer lui-même la ligne
    //   multipart/form-data; boundary=----XYZ
    // Si un Content-Type (json/urlencoded/multipart sans boundary)
    // survit dans les headers hérités d'axios, FastAPI ne peut plus
    // parser le body et renvoie 422 "Field required" sur le champ file.
    const uploadHeaders = { ...headers };
    delete uploadHeaders["Content-Type"];
    delete uploadHeaders["content-type"];
    const resp = await fetch(url, {
      method: (config.method || "get").toUpperCase(),
      body: data,
      headers: uploadHeaders,
      credentials: "include",
    });
    const respHeaders = {};
    resp.headers.forEach((v, k) => (respHeaders[k] = v));
    const ct = (respHeaders["content-type"] || "").toLowerCase();
    let respData;
    if (ct.includes("application/json")) respData = await resp.json();
    else respData = await resp.text();
    if (resp.status >= 200 && resp.status < 300) {
      return {
        data: respData,
        status: resp.status,
        statusText: resp.statusText,
        headers: respHeaders,
        config,
        request: null,
      };
    }
    // Non-2xx → throw like a native axios error so response interceptors
    // + call-site `catch (err)` blocks fire correctly. Without this the
    // frontend silently treats 4xx/5xx as success (e.g. "Course acceptée"
    // toast on a 409 wallet-taken response).
    const err = new Error(
      (respData && respData.detail) ||
        `Request failed with status code ${resp.status}`,
    );
    err.response = {
      data: respData,
      status: resp.status,
      statusText: resp.statusText,
      headers: respHeaders,
      config,
    };
    err.config = config;
    throw err;
  }

  // Binary responses (PDF receipts / invoices) : route through
  // CapacitorHttp with `responseType: "blob"` — WKWebView's `fetch` throws
  // "TypeError: Load failed" intermittently on cross-origin requests to
  // saor.online, whereas CapacitorHttp uses URLSession on iOS which is
  // rock-solid. CapacitorHttp returns the payload as base64 in `data`
  // when responseType is blob, so we rebuild a real Blob for callers
  // relying on axios' `responseType: "blob"` contract.
  if (wantsBinaryResponse) {
    const result = await CapacitorHttp.request({
      url,
      method: (config.method || "get").toUpperCase(),
      headers,
      data,
      responseType: "blob",
      webFetchExtra: { credentials: "include" },
    });
    let respData = result.data;
    if (config.responseType === "blob" && typeof respData === "string") {
      const clean = respData.replace(/^data:[^;]+;base64,/, "");
      const binary = atob(clean);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const mime = (result.headers?.["content-type"] || result.headers?.["Content-Type"] || "application/octet-stream").split(";")[0];
      respData = new Blob([bytes], { type: mime });
    } else if (config.responseType === "arraybuffer" && typeof respData === "string") {
      const clean = respData.replace(/^data:[^;]+;base64,/, "");
      const binary = atob(clean);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      respData = bytes.buffer;
    }
    return {
      data: respData,
      status: result.status,
      statusText: result.status >= 200 && result.status < 300 ? "OK" : "ERROR",
      headers: result.headers || {},
      config,
      request: null,
    };
  }

  // Lazy reference — `CapacitorHttp` is statically imported at the top so
  // it lives inside the main bundle (no async chunk fetch), which matters
  // because the WKWebView on cold start may fail to resolve dynamic chunks
  // before the location handler is fully wired.
  const result = await CapacitorHttp.request({
    url,
    method: (config.method || "get").toUpperCase(),
    headers,
    data,
    // CapacitorHttp uses URLSession sharedCookieStorage on iOS, so the
    // server-side cookies set by /api/auth/login persist across requests
    // without us having to manage them manually.
    webFetchExtra: { credentials: "include" },
  });

  // ⚠️  CapacitorHttp v6 on iOS occasionally returns `result.data` as a
  // raw string even when the server sent `Content-Type: application/json`
  // (see https://github.com/ionic-team/capacitor/issues/6165). Axios
  // callers expect a parsed object, so `data.user` would be undefined
  // and Login.jsx would surface "Réponse inattendue du serveur".
  // Normalize the payload here so every request looks the same as fetch.
  let respData = result.data;
  if (typeof respData === "string" && respData.length > 0) {
    const rawCt =
      result.headers?.["content-type"] ||
      result.headers?.["Content-Type"] ||
      "";
    const looksJson =
      rawCt.toLowerCase().includes("json") ||
      /^\s*[{[]/.test(respData);
    if (looksJson) {
      try {
        respData = JSON.parse(respData);
      } catch (_) {
        /* leave as string if parsing fails */
      }
    }
  }

  return {
    data: respData,
    status: result.status,
    statusText: result.status >= 200 && result.status < 300 ? "OK" : "ERROR",
    headers: result.headers || {},
    config,
    request: null,
  };
}

async function capacitorHttpAdapterWithThrow(config) {
  const resp = await capacitorHttpAdapter(config);
  if (resp.status >= 200 && resp.status < 300) return resp;
  // Non-2xx → throw like the default axios adapter. Otherwise call
  // sites treat 4xx/5xx bodies as success payloads.
  const detail = (resp.data && (resp.data.detail || resp.data.error)) || undefined;
  const err = new Error(detail || `Request failed with status code ${resp.status}`);
  err.response = resp;
  err.config = config;
  return Promise.reject(err);
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 45000,
  ...(isNative ? { adapter: capacitorHttpAdapterWithThrow } : {}),
});

/**
 * Auto-retry transient network failures — WKWebView on iOS emits
 * "TypeError: Load failed" whenever the OS suspends the app briefly,
 * a background sync races the request, or the cellular connection
 * blips. These errors have no `error.response` (never reached the
 * server) so the *safe* set to retry is idempotent methods only.
 * POST/PATCH/DELETE are retried only for CONNECTION errors (never
 * for 5xx that already reached the server, to avoid double writes).
 */
const SAFE_METHODS = new Set(["get", "head", "options"]);
async function retryRequestIfNetworkError(config, originalError, attempt = 1) {
  const method = (config.method || "get").toLowerCase();
  const hasResponse = !!originalError?.response;
  const isNetworkErr =
    !hasResponse &&
    (originalError?.code === "ERR_NETWORK" ||
     originalError?.code === "ECONNABORTED" ||
     /load failed|network|timeout|abort/i.test(originalError?.message || ""));
  if (!isNetworkErr) return null;
  // ⚠️  Ne JAMAIS retry un timeout sur les endpoints /admin/* : si le
  // backend a mis >30s à répondre c'est qu'il est en train de ramer,
  // relancer la même requête double la charge sur l'event-loop déjà
  // bloqué et aggrave le problème. Le user verra directement l'erreur
  // et pourra cliquer "Réessayer" manuellement.
  const url = typeof config.url === "string" ? config.url : "";
  const isAdminPath = url.includes("/admin/");
  const isTimeout = originalError?.code === "ECONNABORTED" ||
    /timeout/i.test(originalError?.message || "");
  if (isAdminPath && isTimeout) return null;
  // Idempotent methods → up to 2 retries. Mutations → 1 retry only
  // (still safer than surfacing a false "Load failed" to the user).
  const maxAttempts = SAFE_METHODS.has(method) ? 3 : 2;
  if (attempt >= maxAttempts) return null;
  // Exponential backoff : 400 ms · 900 ms
  const delay = 300 + attempt * (attempt + 1) * 150;
  await new Promise((r) => setTimeout(r, delay));
  try {
    return await api.request({ ...config, _retryAttempt: attempt + 1 });
  } catch (retryErr) {
    if ((config._retryAttempt || attempt) + 1 < maxAttempts) {
      return retryRequestIfNetworkError(config, retryErr, (config._retryAttempt || attempt) + 1);
    }
    throw retryErr;
  }
}

// Keep Authorization header in sync with localStorage token (fallback for cross-site cookie edge cases)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("saor_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Admin paths : timeout confortable (30s) pour laisser respirer les
  // endpoints lourds en prod (/admin/drivers, /admin/price/rides/live,
  // /admin/price/users-map) qui font des agrégations enrichies.
  // Trop court → l'onglet se vide, trop long → spinner infini si le
  // pod OOM. 30s est un bon compromis mesuré en prod.
  const url = typeof config.url === "string" ? config.url : "";
  if (url.includes("/admin/") && (config.timeout === undefined || config.timeout === 45000)) {
    config.timeout = 30_000;
  }
  return config;
});

// Normalise common HTTP errors into something actionable in toasts
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const url = (error.config?.baseURL || "") + (error.config?.url || "");
    const status = error?.response?.status;
    const method = (error.config?.method || "get").toUpperCase();

    // ─── Auto-retry sur routes /admin/* GET en cas de 520/5xx ────────
    // Le pod prod a OOM-killé par le passé → nginx renvoie 520/502/504
    // le temps du restart (~15-30s). On retry une fois après 1 s pour
    // laisser le pod se relever. Évite que l'admin voie "Serveur
    // injoignable" à chaque petit hoquet.
    const isAdminGet = method === "GET" && typeof error.config?.url === "string" && error.config.url.includes("/admin/");
    const isTransient5xx = status === 520 || status === 502 || status === 503 || status === 504 || status === 522 || status === 524;
    if (isAdminGet && isTransient5xx && !error.config._adminRetried) {
      await new Promise((r) => setTimeout(r, 1000));
      try {
        return await api.request({ ...error.config, _adminRetried: true });
      } catch (_e) { /* fallthrough → sera reporté normalement */ }
    }

    // ─── Auto-retry silent : re-tente les requêtes qui n'ont même pas
    // atteint le serveur (typiquement "TypeError: Load failed" iOS
    // quand l'app repasse en foreground ou que la 4G blippe). L'appel
    // ne renvoie null que si aucun retry n'est applicable — sinon on
    // renvoie la réponse réussie ou on relance après tous les retries.
    if (error.config && !error.config._retryAttempt) {
      const retried = await retryRequestIfNetworkError(error.config, error, 1);
      if (retried) return retried;
    }

    // Always log a structured diagnostic line — helps users send us the
    // console output when something fails on their deployed domain.
    // Exception : /auth/me 401 est normal sur les pages publiques (user
    // non connecté) — on silencie pour éviter le bruit console à chaque
    // visite anonyme de /, /login, /register, etc.
    const isPublicAuthProbe = status === 401 && typeof url === "string" && url.endsWith("/api/auth/me");
    if (!isPublicAuthProbe) {
      // eslint-disable-next-line no-console
      console.error("[API ERROR]", {
        method,
        url,
        status: status ?? "no-response",
        statusText: error?.response?.statusText,
        detail: error?.response?.data?.detail,
        message: error.message,
        code: error.code,
        corsLikely: !error.response && typeof window !== "undefined" && window.location.origin !== (process.env.REACT_APP_BACKEND_URL || ""),
      });
    }

    const detail403 = status === 403 ? error?.response?.data?.detail : null;
    if (typeof detail403 === "string" && detail403.startsWith("Compte suspendu")) {
      try { window.dispatchEvent(new CustomEvent("price:account-suspended", { detail: { message: detail403 } })); } catch (_) { /* ignore */ }
    }
    if (typeof detail403 === "string" && detail403.startsWith("CGU à accepter")) {
      try { window.dispatchEvent(new Event("price:cgu-required")); } catch (_) { /* ignore */ }
    }
    if (typeof detail403 === "string" && detail403.startsWith("Documents requis")) {
      try { window.dispatchEvent(new Event("price:documents-required")); } catch (_) { /* ignore */ }
    }
    if (typeof detail403 === "string" && /^(Documents requis|Accès conducteur suspendu|Documents en attente|Déclaration sur l'honneur)/.test(detail403)) {
      try { window.dispatchEvent(new Event("price:force-offline")); } catch (_) { /* ignore */ }
    }

    if (!error.response) {
      // Diffuse un event global — le composant <OfflineBanner /> monté
      // dans App.js s'affiche alors avec un lien direct vers saor.online
      // pour permettre au user de continuer même quand l'app native
      // n'arrive pas à joindre le backend (WKWebView flaky, etc.).
      try {
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("saor:api-offline", {
            detail: { url, method, code: error.code, message: error.message },
          }));
        }
      } catch (_) { /* ignore */ }
      if (error.code === "ECONNABORTED") {
        error.message = "Connexion trop lente. Vérifiez votre réseau et réessayez.";
      } else {
        error.message = "Connexion internet instable. Vérifiez votre réseau et réessayez.";
      }
    } else if (status === 413) {
      error.response.data = { detail: "Fichiers trop volumineux. Réduisez la taille de vos documents (max ~20 Mo au total)." };
    } else if (status === 504 || status === 408) {
      error.response.data = { detail: "Le serveur a mis trop de temps à répondre. Réessayez." };
    }
    return Promise.reject(error);
  },
);

export function formatApiError(detail) {
  if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
  // Auto-extraction quand on nous passe l'erreur Axios complète au lieu du
  // detail. Évite d'afficher "AxiosError: Request failed with status code 400"
  // à l'utilisateur final.
  if (typeof detail === "object" && (detail.isAxiosError || detail.response || detail.request)) {
    const apiDetail = detail.response?.data?.detail;
    if (apiDetail != null) return formatApiError(apiDetail);
    if (detail.response?.data?.message) return String(detail.response.data.message);
    if (detail.message && !detail.message.startsWith("Request failed")) return detail.message;
    return "Une erreur est survenue. Veuillez réessayer.";
  }
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" · ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/** Convert a File to { filename, mime_type, data(base64) }.
 *  Images are compressed client-side to max 1600px on the long edge, JPEG 0.82
 *  to keep payloads under a few MB — drastically reduces 413 errors. */
export async function fileToBase64(file) {
  if (file && file.type && file.type.startsWith("image/")) {
    try {
      const compressed = await _compressImage(file, 1600, 0.82);
      if (compressed) return compressed;
    } catch (_) { /* fall through to raw */ }
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const base64 = typeof result === "string" ? result.split(",")[1] : "";
      resolve({
        filename: file.name,
        mime_type: file.type || "application/octet-stream",
        data: base64,
      });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function _compressImage(file, maxEdge, quality) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      const scale = Math.min(1, maxEdge / Math.max(width, height));
      width = Math.round(width * scale);
      height = Math.round(height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (!blob) return resolve(null);
          const reader = new FileReader();
          reader.onload = () => {
            const base64 = (reader.result || "").toString().split(",")[1] || "";
            resolve({
              filename: file.name.replace(/\.(png|webp|heic|heif)$/i, ".jpg"),
              mime_type: "image/jpeg",
              data: base64,
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        },
        "image/jpeg",
        quality,
      );
    };
    img.onerror = () => resolve(null);
    const url = URL.createObjectURL(file);
    img.src = url;
  });
}
